import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { FORM } from '../../config/consts';
import InputField from './InputField';
import Option from './Option';
import Tag from './Tag';

interface ICreateMultiAutocompleteProps {
  addAllOptionsId?: string;
  areAllOptionsAdded?: boolean;
  createForm?: React.ReactNode;
  createFormVisibilityKey: string;
  createItemIcon?: React.ReactElement | null;
  customOption?: (renderProps: object, option: object) => React.ReactNode;
  customTag?: (tagValue: object, getTagProps: object) => React.ReactNode;
  disabled?: boolean;
  helperText: string;
  itemIcon?: React.ReactElement | null;
  label?: string;
  name: string;
  newItemId: string;
  newItemId2?: string;
  optionGroupKey: string;
  optionLabelKey: string;
  loading?: boolean;
  options: Array<{ id: string; [key: string]: any }>;
  placeholder?: string;
  scrollableTags?: boolean;
}

interface Item {
  id: string;
}

function CreateMultiAutocomplete({
  addAllOptionsId,
  areAllOptionsAdded,
  createForm,
  createFormVisibilityKey,
  createItemIcon,
  customOption,
  customTag,
  disabled = false,
  helperText,
  itemIcon,
  label,
  loading = false,
  name,
  newItemId,
  newItemId2,
  optionGroupKey,
  optionLabelKey,
  options,
  placeholder,
  scrollableTags = false,
  ...other
}: ICreateMultiAutocompleteProps) {
  const { control, setValue, watch } = useFormContext();
  const { field, fieldState } = useController({ control, name });
  const watchField = watch(name);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  useEffect(() => {
    setValue(
      createFormVisibilityKey,
      !!watchField?.some((item: Item) => item.id === newItemId || item.id === newItemId2)
    );
  }, [watchField]);

  const newItemOption2 = options.find((option) => option.id === newItemId2);
  const newItemOption = options.find((option) => option.id === newItemId);
  const loadingOptionId = 'loading_id';
  const loadingOption = { id: loadingOptionId };
  const resolvedOptions = loading ? [loadingOption] : options;

  return (
    <>
      <Autocomplete
        disabled={disabled}
        {...other}
        {...field}
        autoHighlight
        disableCloseOnSelect
        open={autocompleteOpen}
        onOpen={() => setAutocompleteOpen(true)}
        onClose={() => {
          setAutocompleteOpen(false);
        }}
        getOptionDisabled={(option) => {
          if (option.id === loadingOptionId) {
            return true;
          }

          const addAllSelected = field.value.some((selectedItem: Item) => selectedItem.id === addAllOptionsId);
          const newItemSelected = field.value.some((selectedItem: Item) => selectedItem.id === newItemId);

          if (areAllOptionsAdded && option.id === addAllOptionsId) {
            return true;
          }

          if (addAllSelected) {
            return option.id !== addAllOptionsId;
          } else if (newItemSelected) {
            return option.id === newItemId || option.id === addAllOptionsId;
          }

          return (
            (field.value.some((selectedItem: Item) => selectedItem.id === FORM.NEW_ITEM_ID) &&
              option === newItemOption2) ||
            (field.value.some((selectedItem: Item) => selectedItem.id === FORM.NEW_ITEM_WITHOUT_LOGIN_ID) &&
              option === newItemOption)
          );
        }}
        getOptionLabel={(option) => option?.[optionLabelKey]}
        groupBy={(option) => option?.[optionGroupKey]}
        isOptionEqualToValue={(option, val) => isEqual(option, val)}
        multiple
        onChange={(_e, data, reason, details) => {
          const addAllSelected = data?.find((item: Item) => item.id === addAllOptionsId);
          const newItem = data?.find((item: Item) => item.id === newItemId);
          const newItem2 = data?.find((item: Item) => item.id === newItemId2);

          if (addAllSelected) {
            field.onChange([addAllSelected]);
          } else if (newItem) {
            field.onChange(Array.from(new Set([newItem, ...data])));
          } else if (newItem2) {
            field.onChange(Array.from(new Set([newItem2, ...data])));
          } else {
            field.onChange(data);
          }

          if (
            reason === 'selectOption' &&
            details?.option &&
            (details.option.id === addAllOptionsId ||
              details.option.id === newItemId ||
              details.option.id === newItemId2)
          ) {
            setAutocompleteOpen(false);
          }
        }}
        options={resolvedOptions}
        renderInput={(params) => (
          <InputField
            fieldState={fieldState}
            helperText={helperText}
            label={label}
            params={params}
            placeholder={placeholder}
            scrollableTags={scrollableTags}
          />
        )}
        renderOption={(renderProps, option) =>
          option.id === loadingOptionId ? (
            <ListItem {...renderProps} key={loadingOptionId}>
              <CircularProgress size={20} />
            </ListItem>
          ) : (
            customOption?.(renderProps, option) || (
              <Option
                createItemIcon={createItemIcon}
                itemIcon={itemIcon}
                key={option?.id}
                newItemId={newItemId}
                newItemId2={newItemId2}
                option={option}
                optionLabelKey={optionLabelKey}
                renderProps={renderProps}
              />
            )
          )
        }
        renderTags={(tagValue, getTagProps) =>
          customTag?.(tagValue, getTagProps) || (
            <Tag
              getTagProps={getTagProps}
              newItemId={newItemId}
              newItemId2={newItemId2}
              newItemId2Label={newItemOption2?.tag || newItemOption2?.[optionLabelKey]}
              newItemIdLabel={newItemOption?.tag || newItemOption?.[optionLabelKey]}
              optionLabelKey={optionLabelKey}
              tagValue={tagValue}
            />
          )
        }
        sx={{
          '& .MuiAutocomplete-tag': {
            maxWidth: '100%',
          },
          my: 3,
        }}
      />
      {createForm}
    </>
  );
}

export default CreateMultiAutocomplete;
